// Import React and hooks for state and lifecycle management
import React, { useState, useEffect } from 'react';
// Import React Router hooks for accessing URL params and navigation
import { useParams, useNavigate } from 'react-router-dom';
// Custom hook to access authenticated user
import { useAuth } from '../hooks/useAuth';
// Axios for HTTP requests
import axios from 'axios';

// Component for posting status updates for a course
function PostStatusUpdates() {
  // Get the current authenticated user and loading state
  const { user, isLoading } = useAuth();
  // Get the course_id parameter from the URL (if available)
  const { course_id } = useParams<{ course_id: string }>();
  // React Router navigate function to programmatically redirect
  const navigate = useNavigate();
  // Local state for status update content
  const [content, setContent] = useState('');
  // State for displaying errors
  const [error, setError] = useState<string | null>(null);
  // State for showing submission/loading spinner
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [isLoading, user, navigate]);
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form behavior
    const trimmedContent = content.trim(); // Remove extra spaces
    // Validate content: required field
    if (!trimmedContent) {
      setError('Content is required');
      return;
    }
    // Validate content length: max 1000 characters
    if (trimmedContent.length > 1000) {
      setError('Content cannot exceed 1000 characters');
      return;
    }
    setIsSubmitting(true); // Set submission state
    try {
      // Fetch CSRF token before POST request
      const response = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;

      // Send POST request to create a new status update
      await axios.post(
        '/elearning/api/status_updates/',
        {
          content: trimmedContent,
          course_id: course_id ? parseInt(course_id, 10) : undefined, // Convert course_id to number if exists
        },
        {
          withCredentials: true,
          headers: { 'X-CSRFToken': csrfToken },
        }
      );
      // Redirect back to course page or homepage after successful post
      navigate(course_id ? `/courses/${course_id}` : '/');
    } catch (err: any) {
      // Handle errors returned by backend or network
      const errorMessage =
        err.response?.data?.content?.[0] ||
        err.response?.data?.user?.[0] ||
        err.response?.data?.message ||
        'Failed to post status update';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false); // Reset submission state
    }
  };
  // Render loading spinner while auth state is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Return nothing if user is not available
  if (!user) return null;
  // Main page rendering
  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-3xl">
        {/* Page header with title and back button */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Post Status Update</h1>
          <a
            href={course_id ? `/courses/${course_id}` : '/'}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300"
          >
            Back to {course_id ? 'Course' : 'Homepage'}
          </a>
        </div>
        {/* Display any errors */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
            {error}
          </div>
        )}
        {/* Form container */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit}>
            {/* Textarea for status content */}
            <div className="mb-4">
              <label htmlFor="content" className="block text-gray-900 font-medium mb-2">
                Status Update
              </label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full p-2 border rounded text-gray-900 focus:ring-2 focus:ring-blue-500"
                rows={5}
                maxLength={1000} // Limit characters
              />
              <p className="text-sm text-gray-500 mt-1">{content.length}/1000 characters</p>
            </div>
            {/* Submit button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting} // Disable button during submission
                className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Posting...' : 'Post Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Export the component as default
export default PostStatusUpdates;
